import { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { registerLazy } from '@norkron/web-conformance/register-lazy';

// src/use-virtual.ts

// src/core.ts
function buildOffsets(itemCount, sizeOracle) {
  const offsets = new Array(itemCount + 1);
  offsets[0] = 0;
  let acc = 0;
  for (let i = 0; i < itemCount; i++) {
    const s = sizeOracle(i);
    const sz = Number.isFinite(s) && s > 0 ? s : 0;
    acc += sz;
    offsets[i + 1] = acc;
  }
  return { offsets, totalSize: acc };
}
function findIndexAtOffset(offsets, itemCount, target) {
  if (itemCount <= 0) return 0;
  if (target <= 0) return 0;
  const last = itemCount - 1;
  const lastEnd = offsets[itemCount] ?? 0;
  if (target >= lastEnd) return last;
  let lo = 0;
  let hi = last;
  while (lo < hi) {
    const mid = lo + hi >>> 1;
    const end = offsets[mid + 1] ?? 0;
    if (end <= target) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
}
function computeWindow(args) {
  const { itemCount, offsets, totalSize, scrollOffset, viewportSize, overscan } = args;
  if (itemCount <= 0) {
    return { startIndex: 0, endIndex: 0, items: [], totalSize: 0 };
  }
  const startVisible = findIndexAtOffset(offsets, itemCount, scrollOffset);
  const endVisible = findIndexAtOffset(offsets, itemCount, scrollOffset + viewportSize);
  const startIndex = Math.max(0, startVisible - overscan);
  const endIndex = Math.min(itemCount, endVisible + 1 + overscan);
  const items = [];
  for (let i = startIndex; i < endIndex; i++) {
    const start = offsets[i] ?? 0;
    const end = offsets[i + 1] ?? start;
    items.push({ index: i, start, size: end - start });
  }
  return { startIndex, endIndex, items, totalSize };
}
function scrollOffsetForIndex(args) {
  const { offsets, itemCount, index, scrollOffset, viewportSize } = args;
  if (itemCount <= 0) return scrollOffset;
  if (index < 0 || index >= itemCount) return scrollOffset;
  const start = offsets[index] ?? 0;
  const end = offsets[index + 1] ?? start;
  const viewStart = scrollOffset;
  const viewEnd = scrollOffset + viewportSize;
  if (start < viewStart) return start;
  if (end > viewEnd) return end - viewportSize;
  return scrollOffset;
}

// src/use-virtual.ts
var isBrowser = typeof window !== "undefined" && typeof window.document !== "undefined";
var useIsomorphicLayoutEffect = isBrowser ? useLayoutEffect : useEffect;
function useVirtual(options) {
  const {
    items,
    estimateSize,
    scrollRef,
    overscan = 5,
    orientation = "vertical",
    initialViewportSize = 400
  } = options;
  const [scrollOffset, setScrollOffset] = useState(0);
  const [viewportSize, setViewportSize] = useState(initialViewportSize);
  const measuredRef = useRef(/* @__PURE__ */ new Map());
  const [measureVersion, setMeasureVersion] = useState(0);
  useEffect(() => {
    const map = measuredRef.current;
    let changed = false;
    for (const idx of map.keys()) {
      if (idx >= items.length) {
        map.delete(idx);
        changed = true;
      }
    }
    if (changed) setMeasureVersion((v) => v + 1);
  }, [items.length]);
  const sizeOracle = useMemo(() => {
    const estimateFn = typeof estimateSize === "function" ? estimateSize : () => estimateSize;
    return (index) => {
      const m = measuredRef.current.get(index);
      if (m !== void 0 && m > 0) return m;
      return estimateFn(index);
    };
  }, [estimateSize, measureVersion]);
  const { offsets, totalSize } = useMemo(
    () => buildOffsets(items.length, sizeOracle),
    [items.length, sizeOracle]
  );
  useIsomorphicLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const next = orientation === "vertical" ? el.scrollTop : el.scrollLeft;
      setScrollOffset(next);
    };
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollRef, orientation]);
  useIsomorphicLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const read = () => {
      const next = orientation === "vertical" ? el.clientHeight : el.clientWidth;
      if (next > 0) setViewportSize(next);
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => read());
    ro.observe(el);
    return () => ro.disconnect();
  }, [scrollRef, orientation]);
  const measureElementFactory = useCallback(
    (index) => (node) => {
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const size = orientation === "vertical" ? rect.height : rect.width;
      if (size <= 0) return;
      const prev = measuredRef.current.get(index);
      const rounded = Math.round(size);
      if (prev === rounded) return;
      measuredRef.current.set(index, rounded);
      setMeasureVersion((v) => v + 1);
    },
    [orientation]
  );
  const window2 = useMemo(
    () => computeWindow({
      itemCount: items.length,
      offsets,
      totalSize,
      scrollOffset,
      viewportSize,
      overscan
    }),
    [items.length, offsets, totalSize, scrollOffset, viewportSize, overscan]
  );
  const virtualItems = useMemo(() => {
    const out = [];
    for (const w of window2.items) {
      const item = items[w.index];
      if (item === void 0) continue;
      out.push({
        index: w.index,
        start: w.start,
        size: w.size,
        item,
        measureElement: measureElementFactory(w.index)
      });
    }
    return out;
  }, [window2, items, measureElementFactory]);
  const scrollTo = useCallback(
    (index) => {
      const el = scrollRef.current;
      if (!el) return;
      const next = scrollOffsetForIndex({
        offsets,
        itemCount: items.length,
        index,
        scrollOffset: orientation === "vertical" ? el.scrollTop : el.scrollLeft,
        viewportSize: orientation === "vertical" ? el.clientHeight : el.clientWidth
      });
      if (orientation === "vertical") {
        el.scrollTop = next;
      } else {
        el.scrollLeft = next;
      }
    },
    [scrollRef, offsets, items.length, orientation]
  );
  return {
    virtualItems,
    totalSize,
    scrollTo,
    viewportSize,
    scrollOffset
  };
}

// src/version.ts
var VERSION = "0.1.0";

// src/self-check.ts
var LIB_ID = "@norkron/web-virtual";
var MAX_CONTAINERS = 20;
var MAX_ROWS_PER_CONTAINER = 500;
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function selfCheck(root, _opts) {
  const findings = [];
  try {
    const containers = Array.from(
      root.querySelectorAll(".virtual-list")
    ).slice(0, MAX_CONTAINERS);
    for (const container of containers) {
      const rows = Array.from(
        container.querySelectorAll(
          ".virtual-list__row[data-virtual-index]"
        )
      ).slice(0, MAX_ROWS_PER_CONTAINER);
      if (rows.length < 2) continue;
      const indices = [];
      for (const row of rows) {
        const raw = row.getAttribute("data-virtual-index");
        const n = raw == null ? NaN : Number.parseInt(raw, 10);
        if (Number.isFinite(n)) indices.push(n);
      }
      if (indices.length < 2) continue;
      indices.sort((a, b) => a - b);
      const gaps = [];
      for (let i = 1; i < indices.length; i++) {
        const prev = indices[i - 1];
        const cur = indices[i];
        if (cur === prev) continue;
        if (cur - prev > 1) gaps.push([prev, cur]);
      }
      if (gaps.length === 0) continue;
      findings.push({
        libId: LIB_ID,
        code: "WV_ROW_INDEX_GAP",
        category: "a11y",
        severity: "major",
        message: `Virtualised list rendered non-contiguous data-virtual-index values (gaps: ${gaps.slice(0, 3).map(([a, b]) => `${a}\u2192${b}`).join(", ")}${gaps.length > 3 ? ", \u2026" : ""}). Screen readers and aria-rowindex consumers will mis-announce row positions.`,
        selector: selectorFor(container),
        suggestedFix: "Ensure the rendered window is a contiguous slice of virtualItems; check for filter/predicate logic dropping items mid-window.",
        extras: {
          gapCount: gaps.length,
          firstGap: gaps[0],
          rowsInspected: indices.length
        }
      });
    }
    const strRows = Array.from(
      root.querySelectorAll(
        "[data-norkron-virtual-row][aria-rowindex]"
      )
    ).slice(0, MAX_ROWS_PER_CONTAINER);
    if (strRows.length >= 2) {
      const sorted = strRows.map((el) => ({
        el,
        idx: Number.parseInt(el.getAttribute("aria-rowindex") ?? "", 10)
      })).filter((r) => Number.isFinite(r.idx)).sort((a, b) => a.idx - b.idx);
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const cur = sorted[i];
        if (cur.idx - prev.idx <= 1) continue;
        if (cur.el.hasAttribute("data-norkron-virtual-skip-row")) continue;
        findings.push({
          libId: LIB_ID,
          code: "STR_VIRTUAL_ROW_INDEX_GAP",
          category: "a11y",
          severity: "minor",
          message: `Virtual row aria-rowindex jumps from ${prev.idx} to ${cur.idx} without a [data-norkron-virtual-skip-row] marker on the higher-index row. SR users will mis-announce row position.`,
          selector: selectorFor(cur.el),
          suggestedFix: "Either render the missing intermediate rows, OR stamp the row at the gap boundary with data-norkron-virtual-skip-row to declare the gap as intentional.",
          extras: { from: prev.idx, to: cur.idx }
        });
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "WV_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings;
}

// src/index.ts
registerLazy(
  "@norkron/web-virtual",
  () => selfCheck
);

export { VERSION, buildOffsets, computeWindow, findIndexAtOffset, scrollOffsetForIndex, selfCheck, useVirtual };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map